import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://www.regard-de-nature.fr";

const NaturopatheSommeil = () => {
  useSEO({
    title: "Trouble du sommeil : approche naturelle – Regard de Nature",
    description:
      "Retrouver un sommeil plus serein naturellement avec la naturopathie : hygiène de vie, alimentation, plantes, gestion du stress. Accompagnement personnalisé.",
    canonical: `${SITE}/naturopathe-sommeil`,
  });

  return (
    <SeoPageLayout
      h1="Retrouver un sommeil plus serein naturellement"
      intro="Endormissement qui s'éternise, réveils à 3 h du matin, sensation de ne jamais récupérer ? Quand le sommeil ne vient plus, c'est tout l'équilibre qui se fragilise. Il existe des leviers concrets pour le reconstruire, en douceur."
    >
      <SeoSection title="Quand le sommeil ne vient plus">
        <p>
          Je reçois souvent des personnes qui n'osent plus parler de leurs nuits : « j'ai pris l'habitude »,
          « c'est comme ça depuis des années ». Et pourtant, le sommeil est l'un des piliers de notre vitalité.
          Quand il s'altère, c'est toute l'énergie, l'humeur, la digestion, l'immunité et la concentration qui
          vacillent.
        </p>
        <p>
          La naturopathie n'est pas un traitement de l'insomnie, mais elle propose un cadre d'accompagnement pour
          comprendre ce qui perturbe votre sommeil et mettre en place des ajustements naturels.
        </p>
      </SeoSection>

      <SeoSection title="Ce que vous pouvez retrouver">
        <SeoList
          items={[
            "Un endormissement plus simple, sans tourner pendant des heures.",
            "Des nuits plus continues, avec moins de réveils nocturnes.",
            "Un réveil plus reposé, avec une vraie sensation de récupération.",
            "Une journée plus stable, sans coups de barre brutaux.",
            "Un système nerveux qui sait à nouveau passer en mode pause le soir.",
          ]}
        />
      </SeoSection>

      <SeoSection title="Comprendre votre sommeil">
        <p>
          Le sommeil dépend de nombreux facteurs : rythme de vie, alimentation, exposition à la lumière, stress,
          écrans, hormones, émotions. Dans ma pratique, j'observe que les troubles du sommeil ont presque toujours
          plusieurs racines — c'est en travaillant deux ou trois leviers à la fois que les nuits se réparent
          vraiment.
        </p>
      </SeoSection>

      <SeoSection title="Les pistes que je vous propose d'explorer">
        <SeoSubSection title="Hygiène de vie et rythmes">
          <SeoList
            items={[
              "Régularité des horaires de coucher et de lever",
              "Exposition à la lumière naturelle en journée",
              "Limitation des écrans en soirée",
              "Rituel d'apaisement avant le coucher",
              "Température et obscurité de la chambre",
            ]}
          />
        </SeoSubSection>
        <SeoSubSection title="Alimentation et micronutrition">
          <SeoList
            items={[
              "Repas du soir digeste et apaisant",
              "Apports en magnésium, tryptophane, vitamines B",
              "Limitation des excitants en seconde partie de journée",
              "Hydratation adaptée",
            ]}
          />
        </SeoSubSection>
        <SeoSubSection title="Gestion du stress et des émotions">
          <p>
            Beaucoup de troubles du sommeil sont liés à un{" "}
            <Link to="/naturopathe-stress" className="text-primary underline hover:text-secondary">stress</Link>{" "}
            chronique ou à des émotions non digérées. Respiration, cohérence cardiaque, écriture, marche,
            méditation : autant d'outils que nous pouvons explorer.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Plantes et soutiens naturels">
          <p>
            Selon votre profil, je peux vous orienter vers des plantes adaptogènes, calmantes ou des élixirs
            floraux pour soutenir l'apaisement nocturne. Ces conseils sont éducatifs et personnalisés.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Ce qui guide ma pratique">
        <p>
          Mon objectif n'est pas de vous proposer une « recette miracle », mais de construire avec vous un cadre de
          vie favorable au sommeil, étape par étape. Le sommeil se reconstruit avec patience et bienveillance —
          rarement en une nuit, mais souvent plus vite qu'on ne l'imagine quand on travaille les bons leviers.
        </p>
        <p>
          Je consulte à{" "}
          <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
            Montesquieu-Lauragais
          </Link>{" "}
          et à{" "}
          <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
            Villefranche-de-Lauragais
          </Link>
          .
        </p>
        <p className="font-semibold">
          Les{" "}
          <Link to="/consultations-visio-naturopathe-france" className="text-primary underline hover:text-secondary">
            consultations en visio
          </Link>{" "}
          offrent une solution souple et confortable pour être accompagné, partout en France.
        </p>
        <p className="italic text-foreground/70">
          En cas d'insomnie sévère, persistante ou s'accompagnant d'autres symptômes (apnée du sommeil suspectée,
          dépression, etc.), un avis médical reste indispensable. La naturopathie vient en complément d'un suivi
          médical, jamais en remplacement.
        </p>
      </SeoSection>
    </SeoPageLayout>
  );
};

export default NaturopatheSommeil;
